import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Select,
  Spin,
  Statistic,
  Table,
  Typography,
  Tooltip,
} from "antd";
import {
  FileSearchOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { search } from "../api/institutionApi";
import { createSearchStatementColumns } from "../components/statements/SearchStatements";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const SEARCH_TYPE_OPTIONS = [
  { label: "Бүгд", value: "BOTH" },
  { label: "Ориг хуулга", value: "BANK_STATEMENT" },
  { label: "Хийсвэр хуулга", value: "BANK_REPORT_STATEMENT" },
];

const SEARCH_VALUE_TYPE_OPTIONS = [
  { label: "Дансны дугаар", value: "ACCOUNT" },
  { label: "Дансны нэр", value: "NAME" },
  { label: "Гүйлгээний утга", value: "DESCRIPTION" },
  { label: "Гүйлгээний дүн", value: "AMOUNT" },
];

const TABLE_HEIGHT = 600;

const SearchStatementsPage = () => {
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("BOTH");
  const [searchValueType, setSearchValueType] = useState(undefined);
  const [dateRange, setDateRange] = useState([null, null]);
  const [bankStatements, setBankStatements] = useState([]);
  const [bankReportStatements, setBankReportStatements] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : "";
  const to = dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : "";

  const handleDelete = async (id) => {
    console.log("delete row id =", id);
  };

  const columns = useMemo(
    () =>
      createSearchStatementColumns({
        currency: "MNT",
        onDelete: handleDelete,
      }),
    []
  );

  const normalizeQuery = (value, valueType) => {
    const raw = value?.trim() || "";

    if (!raw) {
      return "";
    }

    if (valueType === "ACCOUNT") {
      return raw.replace(/\s+/g, "");
    }

    if (valueType === "AMOUNT") {
      return raw.replace(/,/g, "").replace(/\s+/g, "");
    }

    return raw;
  };

  const normalizedQuery = normalizeQuery(query, searchValueType);
  const hasDateFilter = Boolean(from || to);
  const canSearch = Boolean(searchValueType && normalizedQuery);

  const handleSearch = async () => {
    if (!canSearch) {
      setError("Хайлтын төрөл болон хайх утгаа оруулна уу");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result = await search({
        query: normalizedQuery,
        searchType,
        valueType: searchValueType,
        from,
        to,
      });

      console.log("search result =", result);

      setBankStatements(
        Array.isArray(result?.bankStatementsResponse)
          ? result.bankStatementsResponse
          : []
      );

      setBankReportStatements(
        Array.isArray(result?.bankReportStatementResponse)
          ? result.bankReportStatementResponse
          : []
      );
    } catch (err) {
      console.error(err);
      setBankStatements([]);
      setBankReportStatements([]);
      setError("Гүйлгээний мэдээлэл ачааллахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setQuery("");
    setSearchType("BOTH");
    setSearchValueType(undefined);
    setDateRange([null, null]);
    setBankStatements([]);
    setBankReportStatements([]);
    setError("");
  };

  const totalCount = bankStatements.length + bankReportStatements.length;

  const searchPlaceholder = (() => {
    switch (searchValueType) {
      case "ACCOUNT":
        return "Дансны дугаар оруулна уу";
      case "NAME":
        return "Байгууллага эсвэл харилцагчийн нэр оруулна уу";
      case "DESCRIPTION":
        return "Гүйлгээний утга оруулна уу";
      case "AMOUNT":
        return "Жишээ: 244.52 эсвэл 12,244.52";
      default:
        return "Эхлээд хайлтын төрөл сонгоно уу";
    }
  })();

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto space-y-5">
        <Card className="rounded-2xl shadow-sm">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <div className="xl:col-span-2">
              <Text className="mb-2 block text-sm font-medium text-slate-700">
                Хайлтын төрөл
              </Text>
              <Select
                size="large"
                value={searchValueType}
                onChange={setSearchValueType}
                className="w-full"
                placeholder="Сонгох"
                options={SEARCH_VALUE_TYPE_OPTIONS}
                allowClear
              />
            </div>

            <div className="xl:col-span-3">
              <Text className="mb-2 block text-sm font-medium text-slate-700">
                Хайлт
              </Text>
              <Input
                allowClear
                size="large"
                value={query}
                prefix={<SearchOutlined className="text-slate-400" />}
                placeholder={searchPlaceholder}
                onChange={(e) => setQuery(e.target.value)}
                onPressEnter={handleSearch}
                disabled={!searchValueType}
              />
            </div>

            <div className="xl:col-span-2">
              <Text className="mb-2 block text-sm font-medium text-slate-700">
                Төрөл
              </Text>
              <Select
                size="large"
                value={searchType}
                onChange={setSearchType}
                className="w-full"
                options={SEARCH_TYPE_OPTIONS}
              />
            </div>

            <div className="xl:col-span-3">
              <Text className="mb-2 block text-sm font-medium text-slate-700">
                Огнооны хүрээ
              </Text>
              <RangePicker
                size="large"
                className="w-full"
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [null, null])}
                format="YYYY-MM-DD"
              />
            </div>

            <div className="xl:col-span-2">
              <Text className="mb-2 block text-sm font-medium text-transparent">
                actions
              </Text>
              <div className="flex gap-2">
                <Button
                  type="primary"
                  size="large"
                  icon={<FileSearchOutlined />}
                  loading={loading}
                  onClick={handleSearch}
                  className="flex-1"
                  disabled={!canSearch}
                >
                  Хайх
                </Button>
                <Tooltip title="Цэвэрлэх">
                  <Button
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    disabled={
                      !query &&
                      !searchValueType &&
                      searchType === "BOTH" &&
                      !hasDateFilter &&
                      bankStatements.length === 0 &&
                      bankReportStatements.length === 0
                    }
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Search failed"
            description={error}
            className="rounded-xl"
          />
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card className="rounded-2xl shadow-sm">
            <Statistic
              title="Нийт үр дүн"
              value={totalCount}
              prefix={<SearchOutlined />}
            />
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <Statistic
              title="Ориг хуулга"
              value={bankStatements.length}
              prefix={<FileTextOutlined />}
            />
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <Statistic
              title="Хийсвэр хуулга"
              value={bankReportStatements.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </div>

        <Spin spinning={loading}>
          <div className="space-y-5">
            <Card className="rounded-2xl shadow-sm" bodyStyle={{ padding: 0 }}>
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <Text className="block text-base font-semibold text-slate-800">
                    Ориг гүйлгээний хуулга
                  </Text>
                  <Text type="secondary">{bankStatements.length} мөр</Text>
                </div>
              </div>

              {bankStatements.length === 0 ? (
                <div className="py-14">
                  <Empty description="Bank statement олдсонгүй" />
                </div>
              ) : (
                <Table
                  rowKey={(record, index) => record?.id || `bank-${index}`}
                  columns={columns}
                  dataSource={bankStatements}
                  pagination={false}
                  scroll={{ x: 1400, y: TABLE_HEIGHT }}
                  sticky
                />
              )}
            </Card>

            <Card className="rounded-2xl shadow-sm" bodyStyle={{ padding: 0 }}>
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <Text className="block text-base font-semibold text-slate-800">
                    Хийсвэр гүйлгээний хуулга
                  </Text>
                  <Text type="secondary">
                    {bankReportStatements.length} мөр
                  </Text>
                </div>
              </div>

              {bankReportStatements.length === 0 ? (
                <div className="py-14">
                  <Empty description="Bank report statement олдсонгүй" />
                </div>
              ) : (
                <Table
                  rowKey={(record, index) => record?.id || `report-${index}`}
                  columns={columns}
                  dataSource={bankReportStatements}
                  pagination={false}
                  scroll={{ x: 1400, y: TABLE_HEIGHT }}
                  sticky
                />
              )}
            </Card>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default SearchStatementsPage;